import { useState } from "react";
import { DriverRow, signedPercent } from "./lib";

type ChartRow = {
  key: string;
  label: string;
  title?: string;
  value: number;
  kind: "row" | "other" | "child" | "fees" | "total";
};

/**
 * Contribution to return as diverging bars from a zero line: blue added to
 * the return, orange took away, grey for fees and cash-flow timing. Every bar
 * also carries its signed value as text, so direction never rests on color.
 * On a phone each row stacks: label and value on one line, the bar under them.
 */
const ContributionChart = ({
  rows,
  other = [],
  fees,
  total,
  labelFor = (row) => row.name,
}: {
  rows: DriverRow[];
  other?: DriverRow[];
  fees: number | null;
  total: number | null;
  labelFor?: (row: DriverRow) => string;
}) => {
  const [otherOpen, setOtherOpen] = useState(false);

  const chartRows: ChartRow[] = [];
  const top = rows.filter((r) => r.contribution >= 0);
  const bottom = rows.filter((r) => r.contribution < 0);
  const toRow = (r: DriverRow, kind: ChartRow["kind"] = "row"): ChartRow => ({
    key: r.key,
    label: labelFor(r),
    title: r.detail,
    value: r.contribution,
    kind,
  });
  chartRows.push(...top.map((r) => toRow(r)));
  if (other.length) {
    chartRows.push({
      key: "__other",
      label: `Everything else (${other.length})`,
      value: other.reduce((sum, r) => sum + r.contribution, 0),
      kind: "other",
    });
    if (otherOpen) chartRows.push(...other.map((r) => toRow(r, "child")));
  }
  chartRows.push(...bottom.map((r) => toRow(r)));
  if (fees != null) {
    chartRows.push({
      key: "__fees",
      label: "Fees & cash-flow timing",
      value: fees,
      kind: "fees",
    });
  }
  if (total != null) {
    chartRows.push({
      key: "__total",
      label: "Total return",
      value: total,
      kind: "total",
    });
  }

  const negSpan = Math.max(0, ...chartRows.map((r) => -r.value));
  const posSpan = Math.max(0, ...chartRows.map((r) => r.value));
  const span = negSpan + posSpan || 1;
  // Room on both sides for the value labels, then the bars to scale.
  const zero = 14 + (negSpan / span) * 66;
  const scale = 66 / span;

  return (
    <div
      className="drivers-chart"
      role="list"
      aria-label="Contribution to return"
    >
      {chartRows.map((row) => {
        const width = Math.abs(row.value) * scale;
        const left = row.value >= 0 ? zero : zero - width;
        const barClass =
          row.kind === "fees" ? "neu" : row.value >= 0 ? "pos" : "neg";
        const toggle = row.kind === "other";
        return (
          <div
            key={row.key}
            role="listitem"
            className={`drivers-chart__row drivers-chart__row_${row.kind}`}
            {...(toggle
              ? {
                  tabIndex: 0,
                  "aria-expanded": otherOpen,
                  onClick: () => setOtherOpen(!otherOpen),
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOtherOpen(!otherOpen);
                    }
                  },
                }
              : {})}
          >
            <span
              className="drivers-chart__label"
              title={row.title ?? row.label}
            >
              {toggle ? (otherOpen ? "▼ " : "▶ ") : ""}
              {row.label}
            </span>
            <span className="drivers-chart__value-sm tabular-nums">
              {signedPercent(row.value)}
            </span>
            <span className="drivers-chart__track">
              <span
                className="drivers-chart__zero"
                style={{ left: `${zero}%` }}
              />
              <span
                className={`drivers-chart__bar drivers-chart__bar_${barClass}`}
                style={{ left: `${left}%`, width: `${Math.max(width, 0.4)}%` }}
              />
              <span
                className="drivers-chart__value tabular-nums"
                style={
                  row.value >= 0
                    ? { left: `calc(${zero + width}% + 6px)` }
                    : { right: `calc(${100 - (zero - width)}% + 6px)` }
                }
              >
                {signedPercent(row.value)}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default ContributionChart;
